import { useCallback, useEffect, useMemo } from 'react'
import { useRouter } from 'next/router'
import useTranslation from 'next-translate/useTranslation'
import moment from 'moment'

import { appSetLanguage } from '../store/appSlice'
import { setAcceptLanguage } from '../../../api/client/Request'
import { SupportedLanguage } from '../constants'
import useAppDispatch from '../../../store/hooks/useAppDispatch'
import localStorageUtils from '../utils/localStorageUtils'

function getBrowserLanguage() {
  const navigatorLanguage = navigator.language || navigator.languages?.[0]

  if (navigatorLanguage) {
    const browserLang = navigatorLanguage.toLowerCase()

    if (browserLang.startsWith('sv')) {
      return SupportedLanguage.Swedish
    }

    if (browserLang.startsWith('nb') || browserLang.startsWith('no')) {
      return SupportedLanguage.Norwegian
    }

    if (browserLang.startsWith('en')) {
      return SupportedLanguage.English
    }

    return null
  }

  return null
}

function getUserPreferredLanguage(): string | null {
  return localStorageUtils.getItem<string>('userPreferredLanguage')
}

function setUserPreferredLanguage(language: string) {
  localStorageUtils.setItem('userPreferredLanguage', language)
}

function updateMomentLocale(locale: string) {
  switch (locale) {
    case SupportedLanguage.Swedish:
      moment.locale('sv')
      break
    case SupportedLanguage.Norwegian:
      moment.locale('nb')
      break
    default:
      moment.locale('en-gb')
  }
}

interface UseChangeLanguageOptions {
  preferBrowserLanguage?: boolean
}

export default function useChangeLanguage({
  preferBrowserLanguage,
}: UseChangeLanguageOptions = {}) {
  const { lang } = useTranslation()

  const router = useRouter()

  const dispatch = useAppDispatch()

  const setLanguage = useCallback(
    (language: string) => {
      dispatch(appSetLanguage(language))
      setAcceptLanguage(language)
      updateMomentLocale(language)
    },
    [dispatch],
  )

  const updateRouterLocale = useCallback(
    (locale: string) => {
      const { asPath, pathname, query } = router
      // Replace the current route with the new language
      router.replace({ pathname, query }, asPath, {
        locale,
      })
    },
    [router],
  )

  const handleChangeLanguage = useCallback(
    (nextLanguage: string) => {
      if (preferBrowserLanguage) {
        // Store the user's language preference
        setUserPreferredLanguage(nextLanguage)
      }
      // Update the language in the router
      updateRouterLocale(nextLanguage)
      // Update the language in the app
      setLanguage(nextLanguage)
    },
    [preferBrowserLanguage, setLanguage, updateRouterLocale],
  )

  useEffect(() => {
    const browserLanguage = getBrowserLanguage()

    const storedLanguage = getUserPreferredLanguage()

    // Set the initial language in the app

    if (preferBrowserLanguage) {
      const preferredLanguage = storedLanguage || browserLanguage

      if (preferredLanguage && preferredLanguage !== lang) {
        updateRouterLocale(preferredLanguage)
      }

      setLanguage(preferredLanguage || lang)
    } else {
      setLanguage(lang)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lang, preferBrowserLanguage, setLanguage])

  return useMemo(
    () => ({
      language: lang,
      changeLanguage: handleChangeLanguage,
    }),
    [handleChangeLanguage, lang],
  )
}
